import { Injectable } from '@angular/core'
import { Actions, Effect, ofType } from '@ngrx/effects'
import {
  catchError,
  concatMap,
  debounceTime,
  exhaustMap,
  groupBy,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  toArray,
} from 'rxjs/operators'
import { combineLatest, forkJoin, of } from 'rxjs'
import { select, Store } from '@ngrx/store'
import * as R from 'ramda'
import * as lodash from 'lodash'
import { Router } from '@angular/router'
import {
  addRental,
  addRentalComplete,
  addRentalImages,
  addRentalImagesComplete,
  addTagsToRental,
  addTagsToRentalComplete,
  archiveRental,
  archiveRentalComplete,
  deleteRental,
  deleteRentalComplete,
  editRentalImage,
  editRentalImageComplete,
  loadArchivedRentals,
  loadArchivedRentalsComplete,
  loadRental,
  loadRentalComplete,
  loadRentalImages,
  loadRentalImagesComplete,
  loadRentals,
  loadRentalsComplete,
  loadSubdomain,
  loadSubdomainComplete,
  makeRentalImagePrimary,
  removeRentalImage,
  removeRentalImageComplete,
  restrictUser,
  restrictUserAll,
  restrictUserAllComplete,
  restrictUserComplete,
  restrictRentalUsers,
  restrictRentalUsersComplete,
  toggleOwnerRentals,
  toggleOwnerRentalsComplete,
  unArchiveRental,
  unArchiveRentalComplete,
  unRestrictUser,
  unRestrictUserAll,
  unRestrictUserAllComplete,
  unRestrictUserComplete,
  updateAllRentalsCheckTimes,
  updateAllRentalsCheckTimesComplete,
  updateRentalOwners,
  updateRentalOwnersComplete,
  updateRental,
  updateRentalBaseRate,
  updateRentalBaseRateComplete,
  updateRentalComplete,
  updateRentalCustomFields,
  updateRentalCustomFieldsComplete,
  updateRentalDetails,
  updateRentalDetailsComplete,
  updateRentalGPS,
  updateRentalGPSComplete,
  updateRentalImageOrder,
  updateRentalImageOrderComplete,
  updateRentalInstructions,
  updateRentalInstructionsComplete,
  updateRentalTaxes,
  updateRentalTaxesComplete,
  updateRentalPaymentSchedule,
  updateRentalPaymentScheduleComplete,
  loadRentalsImageCounts,
  loadRentalsImageCountsComplete,
  loadRentalsImages,
  loadRentalsImagesComplete,
  UpdateRentalStatus,
  UpdateRentalStatusComplete,
  removeRentalImages,
  removeRentalImagesComplete,
  loadRentalsPrimaryImages,
  loadRentalsPrimaryImagesComplete,
  UpdateRentalsStatus,
  UpdateRentalsStatusComplete,
  loadRentalRates,
  loadRentalRatesComplete,
  setRentalChildren,
  setRentalChildrenComplete,
  setRentalSubUnits,
  setRentalSubUnitsComplete,
  addSubUnitRentalsComplete,
  addSubUnitRentals,
  UpdateSubunitRentals,
  UpdateSubunitRentalsComplete,
  updateRentalMultiUnitSettings,
  updateRentalMultiUnitSettingsComplete,
  copyRentalBookingFees,
  copyRentalImages,
  copyRentalBookingFeesComplete,
  copyRentalImagesComplete,
} from './rental.actions'
import { RentalService } from './rental.service'
import { EditRentalImageRequest } from './rental.interfaces'
import { Rental } from './rental.models'
import {
  selectAllRentals,
  selectIsPrimaryImagesLoaded,
  selectRentalEntities,
  selectRentalMultiUnitGroups,
  selectRentalParentChildrenGroups,
} from './rental.selectors'
import { Update } from '@ngrx/entity'
import { Toaster } from '../../services'
import { ActionFailed, ActionSkipped } from '../utility'
import { currencies } from '../../constants'
import { selectCurrentAccount } from '../account'
import { selectOnce } from '../../rx-operators'

@Injectable()
export class RentalEffects {
  @Effect()
  loadRentals$ = this.actions$.pipe(
    ofType(loadRentals),
    // The same load is dispatched several times within one navigation (app-component default
    // set, route guards, SubscriptionPermissionGuard) before the loaded flag flips, so
    // identical requests are deduped for as long as one is in flight; `less` variants that
    // hit a different URL get their own group and are not blocked
    groupBy(({ less }) => (less === false ? 'full' : 'norates')),
    mergeMap((group$) =>
      group$.pipe(
        exhaustMap(({ less }) =>
          this.rentals.all(less).pipe(
            // Primary images (a 3-4s all-rentals payload) are chained only while never loaded —
            // rental refreshes must not re-download the whole collection; pages that need
            // images up front declare RentalsPrimaryImageGuard themselves
            switchMap((rentals) =>
              combineLatest([
                this.store.pipe(selectOnce(selectIsPrimaryImagesLoaded)),
                this.store.pipe(selectOnce(selectRentalEntities)),
              ]).pipe(
                switchMap(([imagesLoaded, rentalEntities]) => {
                  const rentalIds = imagesLoaded
                    ? rentals.filter((rental) => !rentalEntities[rental.id]).map((rental) => rental.id)
                    : rentals.map((rental) => rental.id)

                  return [
                    loadRentalsComplete({ rentals }),
                    ...(rentalIds.length ? [loadRentalsPrimaryImages({ rentalIds })] : []),
                  ]
                })
              )
            ),
            catchError((error) => of(ActionFailed({ error })))
          )
        )
      )
    )
  )
  @Effect()
  loadRentalRates$ = this.actions$.pipe(
    ofType(loadRentalRates),
    // Several components may request rates concurrently (different rentals or a full load),
    // so loads must run in parallel instead of cancelling each other, while identical
    // requests for the same pkeys set are deduped for as long as one is in flight
    groupBy(({ pkeys }) => (pkeys?.length ? [...pkeys].sort().join(',') : 'all')),
    mergeMap((group$) =>
      group$.pipe(
        exhaustMap(({ pkeys }) =>
          this.rentals.getOnlyRatesMerged(pkeys).pipe(
            map((rentals) =>
              loadRentalRatesComplete({ updates: rentals.map((t) => ({ id: t.id, changes: { rates: t.rates } })) })
            ),
            catchError((error) => of(ActionFailed({ error })))
          )
        )
      )
    )
  )

  @Effect()
  loadRental$ = this.actions$.pipe(
    ofType(loadRental),
    switchMap(({ id }) =>
      this.rentals.get(id).pipe(
        map((rental) => loadRentalComplete({ rental })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  loadRentalsImageCounts$ = this.actions$.pipe(
    ofType(loadRentalsImageCounts),
    switchMap(({ ids }) =>
      this.rentals.loadRentalsImageCounts(ids).pipe(
        map((counts) =>
          loadRentalsImageCountsComplete({
            updates: R.map((c) => ({ id: c.rental_id, changes: { imageCount: c.count } }), counts),
          })
        ),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  loadRentalsImages$ = this.actions$.pipe(
    ofType(loadRentalsImages),
    switchMap(({ ids }) =>
      this.rentals.loadRentalsImages(ids).pipe(
        map((images) => {
          const imagesByRentalId = lodash.groupBy(images, (i) => i.rentalId)
          return loadRentalsImagesComplete({
            updates: Object.entries(imagesByRentalId).map(([id, rentalImages]) => ({
              id,
              changes: { imageCount: rentalImages.length, images: rentalImages },
            })),
          })
        }),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  archiveRental$ = this.actions$.pipe(
    ofType(archiveRental),
    switchMap(({ rental }) =>
      this.rentals.archiveRental(rental.id).pipe(
        map((rental) => archiveRentalComplete({ rental })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  setChildren$ = this.actions$.pipe(
    ofType(setRentalChildren),
    switchMap(({ rentalId, addedChildren, removedChildren, silent }) =>
      combineLatest([
        !!addedChildren.length ? this.rentals.setRentalsParent(addedChildren, rentalId) : of([]),
        !!removedChildren.length ? this.rentals.clearRentalsParent(removedChildren) : of([]),
      ]).pipe(
        map(() =>
          setRentalChildrenComplete({
            updates: [
              ...addedChildren.map((id) => ({ id, changes: { parent_id: rentalId } })),
              ...removedChildren.map((id) => ({ id, changes: { parent_id: undefined } })),
            ],
          })
        ),
        tap(() => !silent && this.toast.success($localize`Rental updated successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  clearParents$ = this.actions$.pipe(
    ofType(setRentalChildrenComplete),
    debounceTime(100),
    switchMap(() => this.store.pipe(selectOnce(selectRentalParentChildrenGroups))),
    map((groups) => {
      const clearParentIds = lodash
        .toPairs(groups)
        .filter(([parentId, children]) => !children.length)
        .map(([id]) => id)
      if (!clearParentIds.length) {
        return ActionSkipped()
      }
      this.store.dispatch(
        setRentalChildren({
          rentalId: '',
          addedChildren: [],
          removedChildren: clearParentIds,
          silent: true,
        })
      )
    })
  )

  @Effect()
  setSubUnit$ = this.actions$.pipe(
    ofType(setRentalSubUnits),
    switchMap(({ rentalId, addedChildren, removedChildren, silent }) =>
      combineLatest([
        !!addedChildren.length ? this.rentals.setRentalsMainUnit(addedChildren, rentalId) : of([]),
        !!removedChildren.length ? this.rentals.clearRentalsMainUnit(removedChildren) : of([]),
      ]).pipe(
        map(() =>
          setRentalSubUnitsComplete({
            updates: [
              ...addedChildren.map((id) => ({ id, changes: { multi_unit_id: rentalId, multi_unit_status: 1 } })),
              ...removedChildren.map((id) => ({ id, changes: { multi_unit_id: undefined } })),
            ],
          })
        ),
        tap(() => !silent && this.toast.success($localize`Rental updated successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  clearMulti$ = this.actions$.pipe(
    ofType(setRentalSubUnitsComplete),
    debounceTime(100),
    switchMap(() => this.store.pipe(selectOnce(selectRentalMultiUnitGroups))),
    map((groups) => {
      const clearParentIds = lodash
        .toPairs(groups)
        .filter(([parentId, children]) => !children.length)
        .map(([id]) => id)

      if (!clearParentIds.length) {
        return ActionSkipped()
      }

      return setRentalSubUnits({
        rentalId: '',
        addedChildren: [],
        removedChildren: clearParentIds,
        silent: true,
      })
    })
  )

  @Effect()
  updateMultiunitSettings$ = this.actions$.pipe(
    ofType(updateRentalMultiUnitSettings),
    switchMap(({ rentalId, settings, silent }) =>
      this.rentals.updateMultiUnitSettings(rentalId, settings).pipe(
        tap(() => !silent && this.toast.success($localize`Rental updated successfully.`)),
        map((rental) => updateRentalMultiUnitSettingsComplete({ update: { id: rental.id, changes: { ...rental } } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  updateStatus$ = this.actions$.pipe(
    ofType(UpdateRentalStatus),
    switchMap(({ rentalId, status }) =>
      this.rentals.updateStatus(rentalId, status).pipe(
        tap(() => this.toast.success($localize`Rental status updated successfully.`)),
        map((rental) => UpdateRentalStatusComplete({ update: { id: rental.id, changes: { ...rental } } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  updateRentalsStatus$ = this.actions$.pipe(
    ofType(UpdateRentalsStatus),
    switchMap(({ rentalIds, status }) =>
      of(...rentalIds).pipe(
        concatMap((id) => this.rentals.updateStatus(id, status)),
        toArray(),
        map((rentals) => UpdateRentalsStatusComplete({ updates: rentals.map((r) => ({ id: r.id, changes: r })) })),
        tap(() => this.toast.success($localize`Rental status updated successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  updateRentalsName$ = this.actions$.pipe(
    ofType(UpdateSubunitRentals),
    switchMap(({ items }) =>
      of(...items).pipe(
        concatMap(({ id, payload }) => this.rentals.updateSubunit(id, payload)),
        toArray(),
        map((rentals) => UpdateSubunitRentalsComplete({ updates: rentals.map((r) => ({ id: r.id, changes: r })) })),
        tap(() => this.toast.success($localize`Rentals updated successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  loadArchivedRental$ = this.actions$.pipe(
    ofType(loadArchivedRentals),
    switchMap(() =>
      this.rentals.getArchivedRentals().pipe(
        map((rentals) => loadArchivedRentalsComplete({ rentals })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  unArchiveRental$ = this.actions$.pipe(
    ofType(unArchiveRental),
    switchMap(({ rental }) =>
      this.rentals.unArchiveRental(rental.id).pipe(
        map((rental) => unArchiveRentalComplete({ rental })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  loadImages$ = this.actions$.pipe(
    ofType(loadRentalImages),
    mergeMap(({ id }) =>
      this.rentals.images(id).pipe(
        map((images) => loadRentalImagesComplete({ update: { id, changes: { images } } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  loadPrimaryImages$ = this.actions$.pipe(
    ofType(loadRentalsPrimaryImages),
    // Every caller loads the whole collection (the guard passes no ids, loadRentals$ chains
    // all ids), so concurrent dispatches are identical — keep a single request in flight
    exhaustMap(({ rentalIds }) =>
      this.rentals.getPrimaryImages(rentalIds).pipe(
        map((images) =>
          loadRentalsPrimaryImagesComplete({
            updates: images.map((image) => ({ id: image.rentalId, changes: { images: [image] } })),
          })
        ),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  loadSubDomain$ = this.actions$.pipe(
    ofType(loadSubdomain),
    switchMap(() =>
      this.rentals.getSubDomain().pipe(
        map((domain) => loadSubdomainComplete({ domain })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  addImage$ = this.actions$.pipe(
    ofType(addRentalImages),
    switchMap(({ rentalId, data }) => {
      return of(...data).pipe(
        concatMap((image) => this.rentals.addImage({ ...image, rental_id: rentalId })),
        toArray(),
        tap(() => this.toast.success($localize`Rental images added successfully.`)),
        switchMap((images) => [addRentalImagesComplete({ images }), loadRentalImages({ id: rentalId })]),
        catchError((error) => of(ActionFailed({ error })))
      )
    })
  )

  @Effect()
  removeImage$ = this.actions$.pipe(
    ofType(removeRentalImage),
    switchMap(({ id }) =>
      this.rentals.deleteImage(id).pipe(
        switchMap((image) => [loadRentalImages({ id: image.rentalId }), removeRentalImageComplete({ image })]),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  removeImages$ = this.actions$.pipe(
    ofType(removeRentalImages),
    switchMap(({ ids }) =>
      this.rentals.deleteImages(ids).pipe(
        switchMap((images) => [loadRentalImages({ id: images[0].rentalId }), removeRentalImagesComplete({ images })]),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  primaryImage$ = this.actions$.pipe(
    ofType(makeRentalImagePrimary),
    map(({ rentalId, primaryImageId, images }) => {
      let primaryOrder = 0
      images.map((image) => {
        if (image.id === primaryImageId) {
          primaryOrder = image.order
        }
      })

      const data = {
        primary: { rental: rentalId, image: primaryImageId },
        reorders: [{ id: primaryImageId, order: 0 }],
      }

      images.map((image) => {
        if (image.order < primaryOrder) {
          data.reorders.push({ id: image.id, order: image.order + 1 })
        }
      })

      return data
    }),
    switchMap((data) =>
      this.rentals.primaryImage(data.primary.rental, data.primary.image).pipe(
        switchMap(() =>
          forkJoin(data.reorders.map((imageData) => this.rentals.editImage(imageData.id, { order: imageData.order })))
        ),
        switchMap((images) => [loadRentalImages({ id: images[0].rentalId })]),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  editRentalImage$ = this.actions$.pipe(
    ofType(editRentalImage),
    map(({ imageId, category, description }) => {
      const data: { id: string; data: EditRentalImageRequest } = { id: imageId, data: { metadata: {} } }
      if (category) {
        data.data.metadata.category = category
      }
      if (description) {
        data.data.metadata.description = description
      }
      return data
    }),
    switchMap((request) =>
      this.rentals.editImage(request.id, request.data).pipe(
        switchMap((image) => [loadRentalImages({ id: image.rentalId }), editRentalImageComplete({ image })]),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  addRental$ = this.actions$.pipe(
    ofType(addRental),
    switchMap(({ payload }) => {
      return this.store.pipe(
        selectOnce(selectCurrentAccount),
        map((account) => {
          payload.currency = payload.currency || account.currency || currencies[0]
          payload.checkin = payload.checkin || account.attributes?.checkin
          payload.checkout = payload.checkout || account.attributes?.checkin
          return payload
        })
      )
    }),
    switchMap((payload) =>
      this.rentals.add(payload).pipe(
        map((rental) => addRentalComplete({ rental })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  addRentalUnits$ = this.actions$.pipe(
    ofType(addSubUnitRentals),
    switchMap(({ items }) =>
      this.rentals.addSubunits(items).pipe(
        map((rentals) => addSubUnitRentalsComplete({ rentals })),
        tap(() => this.toast.success($localize`Subunits added successfully`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  updateRental$ = this.actions$.pipe(
    ofType(updateRental),
    concatMap(({ payload, rentalId }) =>
      this.rentals.update(rentalId, payload).pipe(
        map((rental) => updateRentalComplete({ update: { id: rental.id, changes: rental } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )
  @Effect()
  updateRentalGPS$ = this.actions$.pipe(
    ofType(updateRentalGPS),
    concatMap(({ gps, rentalId }) =>
      this.rentals.updateGPS(rentalId, gps).pipe(
        map((rental) => updateRentalGPSComplete({ update: { id: rental.id, changes: rental } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  updateRentalTaxes$ = this.actions$.pipe(
    ofType(updateRentalTaxes),
    concatMap(({ taxes, rentalId, silent }) =>
      this.rentals.updateTaxes(rentalId, taxes, silent).pipe(
        map((rental) => updateRentalTaxesComplete({ update: { id: rentalId, changes: rental } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  updateRentalPaymentSchedule$ = this.actions$.pipe(
    ofType(updateRentalPaymentSchedule),
    concatMap(({ payload, rentalId, silent }) =>
      this.rentals.updatePaymentSchedule(payload, rentalId, silent).pipe(
        map((response) => updateRentalPaymentScheduleComplete({ update: { id: rentalId, changes: response } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  removeRental$ = this.actions$.pipe(
    ofType(deleteRental),
    switchMap(({ rentalId, redirectPath }) =>
      this.rentals.remove(rentalId).pipe(
        map((id) => {
          if (redirectPath) {
            this.router.navigate([redirectPath])
          }
          return deleteRentalComplete({ id })
        }),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  updateOwners$ = this.actions$.pipe(
    ofType(updateRentalOwners),
    concatMap(({ rental, addOwnerIds, removeOwnerIds, currentOwnerIds }) =>
      this.rentals.removeOwners(rental, removeOwnerIds).pipe(
        switchMap(() => this.rentals.addOwners(rental, addOwnerIds)),
        tap(() => this.toast.success($localize`Owner updated successfully`)),
        map(() => updateRentalOwnersComplete({ update: { id: rental.id, changes: { owners: currentOwnerIds } } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  toggleOwnerRentals = this.actions$.pipe(
    ofType(toggleOwnerRentals),
    switchMap(({ ownerId, rentals, selected }) => {
      const rentalIds = rentals.map((r) => r.id)
      return (
        selected
          ? this.rentals.addRentalsToOwner(ownerId, rentalIds)
          : this.rentals.removeRentalsToOwner(ownerId, rentalIds)
      ).pipe(map(() => ({ ownerId, rentals, selected })))
    }),
    tap(() => this.toast.success($localize`Owner updated successfully`)),
    map(({ ownerId, rentals, selected }) =>
      R.map(
        (r) => ({
          id: r.id,
          changes: {
            owners: selected ? [...(r.owners || []), ownerId] : (r.owners || []).filter((id) => id !== ownerId),
          },
        }),
        rentals
      )
    ),
    map((updates) => toggleOwnerRentalsComplete({ updates })),
    catchError((error) => of(ActionFailed({ error })))
  )

  @Effect()
  updateCustom$ = this.actions$.pipe(
    ofType(updateRentalCustomFields),
    concatMap(({ payload, rentalId }) =>
      this.rentals.updateCustom(rentalId, payload).pipe(
        map(() => updateRentalCustomFieldsComplete({ update: { id: rentalId, changes: { ...payload } } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  updateDetails$ = this.actions$.pipe(
    ofType(updateRentalDetails),
    concatMap(({ payload, rentalId }) =>
      this.rentals.updateDetails(rentalId, payload).pipe(
        map((rental) => updateRentalDetailsComplete({ update: { id: rental.id, changes: { ...rental } } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  updateInstructions$ = this.actions$.pipe(
    ofType(updateRentalInstructions),
    concatMap(({ payload, rentalId }) =>
      this.rentals.updateInstructions(rentalId, payload).pipe(
        map((rental) => updateRentalInstructionsComplete({ update: { id: rental.id, changes: { ...rental } } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  copyFees$ = this.actions$.pipe(
    ofType(copyRentalBookingFees),
    switchMap(({ srcRentalId, dstRentalIds, resetDstRentals, silent }) =>
      this.rentals.copyRentalFees(srcRentalId, dstRentalIds, resetDstRentals).pipe(
        tap(() => !silent && this.toast.success($localize`Booking fees copied successfully.`)),
        map((rental) => copyRentalBookingFeesComplete()),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  copyImages$ = this.actions$.pipe(
    ofType(copyRentalImages),
    switchMap(({ srcRentalId, dstRentalIds, resetDstRentals, silent }) =>
      this.rentals.copyRentalImages(srcRentalId, dstRentalIds, resetDstRentals).pipe(
        tap(() => !silent && this.toast.success($localize`Rental images copied successfully.`)),
        map((rental) => copyRentalImagesComplete()),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  updateImageOrder$ = this.actions$.pipe(
    ofType(updateRentalImageOrder),
    switchMap(({ images, rental }) =>
      this.rentals.updateImagesOrder(images).pipe(
        map((images) => {
          return R.map((rentalImage) => {
            const updatedImage = R.find((u) => u.id === rentalImage.id, images)
            if (!R.isNil(updatedImage)) {
              return updatedImage
            } else {
              return rentalImage
            }
          }, rental.images)
        }),
        map((images) => ({ id: rental.id, changes: { images } })),
        map((update) => updateRentalImageOrderComplete({ update })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  updateBaseRate$ = this.actions$.pipe(
    ofType(updateRentalBaseRate),
    concatMap(({ payload, rentalId }) =>
      this.rentals.updateBaseRate(rentalId, payload).pipe(
        map((rental) => updateRentalBaseRateComplete({ update: { id: rental.id, changes: { ...rental } } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  updateRestrictions$ = this.actions$.pipe(
    ofType(restrictRentalUsers),
    concatMap(({ rental, addUserIds, removeUserIds, currentUserIds }) =>
      this.rentals.unrestrictUsers(rental, removeUserIds).pipe(
        switchMap(() => this.rentals.restrictUsers(rental, addUserIds)),
        tap(() => this.toast.success($localize`User restrictions updated successfully`)),
        map(() =>
          restrictRentalUsersComplete({
            update: {
              id: rental.id,
              changes: { restrictedUsers: currentUserIds },
            },
          })
        ),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  addTagsToRental$ = this.actions$.pipe(
    ofType(addTagsToRental),
    switchMap(({ rental, tags, newTags }) =>
      this.rentals.addTagsToRental(rental, tags).pipe(
        map(({ id, tags }) => addTagsToRentalComplete({ update: { id: rental.id, changes: { tags: tags } } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  restrictUser$ = this.actions$.pipe(
    ofType(restrictUser),
    switchMap(({ rental, userId }) =>
      this.rentals.restrictUser(rental, userId).pipe(
        tap(() => this.toast.success($localize`Rental restricted successfully.`)),
        map(() => R.pipe(R.append(userId), R.uniq)(rental.restrictedUsers)),
        map((restrictedUsers: string[]) =>
          restrictUserComplete({
            update: {
              id: rental.id,
              changes: { restrictedUsers },
            },
          })
        ),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  restrictUserAll$ = this.actions$.pipe(
    ofType(restrictUserAll),
    switchMap(({ rentals, userId }) =>
      this.rentals.restrictUserAll(rentals, userId).pipe(
        tap(() => this.toast.success($localize`Rentals restricted successfully.`)),
        map(() =>
          R.map(
            (r) => ({
              id: r.id,
              changes: {
                restrictedUsers: <string[]>R.pipe(R.append(userId), R.uniq)(r.restrictedUsers),
              },
            }),
            rentals
          )
        ),
        map((updates) => restrictUserAllComplete({ updates })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  unRestrictUser$ = this.actions$.pipe(
    ofType(unRestrictUser),
    switchMap(({ rental, userId }) =>
      this.rentals.unrestrictUser(rental, userId).pipe(
        tap(() => this.toast.success($localize`Rental unrestricted successfully.`)),
        map(() => R.filter((userId) => userId !== userId)(rental.restrictedUsers)),
        map((restrictedUsers: string[]) =>
          unRestrictUserComplete({
            update: {
              id: rental.id,
              changes: { restrictedUsers },
            },
          })
        ),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  unRestrictUserAll$ = this.actions$.pipe(
    ofType(unRestrictUserAll),
    switchMap(({ rentals, userId }) =>
      this.rentals.unRestrictUserAll(rentals, userId).pipe(
        tap(() => this.toast.success($localize`Rentals unrestricted successfully.`)),
        map(() =>
          R.map(
            (r) => ({
              id: r.id,
              changes: {
                restrictedUsers: <string[]>R.filter((userId) => userId !== userId)(r.restrictedUsers),
              },
            }),
            rentals
          )
        ),
        map((updates) => unRestrictUserAllComplete({ updates })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  updateAllRentalsCheckTimes$ = this.actions$.pipe(
    ofType(updateAllRentalsCheckTimes),
    switchMap(({ checkin, checkout }) =>
      this.rentals.updateCheckTimeForAllRentals({ checkin, checkout }).pipe(
        tap(() => this.toast.success($localize`Check-in / check-out times updated successfully for all rentals.`)),
        switchMap((omitIds: string[]) => {
          return this.store.pipe(select(selectAllRentals), take(1)).pipe(
            map((rentals: Rental[]): Update<Rental>[] => {
              if (omitIds && omitIds.length) {
                rentals = R.filter((r) => omitIds.indexOf(r.id) === -1, rentals || [])
              }
              return R.map((r) => ({ id: r.id, changes: { checkin, checkout } }), rentals)
            })
          )
        }),
        map((updates) => updateAllRentalsCheckTimesComplete({ updates })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  constructor(
    private actions$: Actions,
    private router: Router,
    private toast: Toaster,
    private store: Store<any>,
    private rentals: RentalService
  ) {}
}
